<template>
  <div class="d-flex flex-column min-vh-100">
    <template v-if="showLayout">
      <!-- Navigation -->
      <header>
        <nav class="navbar navbar-expand-lg navbar-light bg-white fixed-top shadow-sm">
          <div class="container">
            <router-link :to="getRoutePath('/')" class="navbar-brand logo-font">kikuflow</router-link>
            <BNavbarToggle target="navbarNav" />
            <BCollapse id="navbarNav" is-nav>
              <ul class="navbar-nav me-auto mb-2 mb-lg-0">
                <li class="nav-item">
                  <a class="nav-link" :href="getRoutePath('/superpowers')">{{ t('components.navbar.features') }}</a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :href="getRoutePath('/comparison')">{{ t('components.navbar.comparison') }}</a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :href="getRoutePath('/pricing')">{{ t('components.navbar.pricing') }}</a>
                </li>
              </ul>
              <div class="d-flex align-items-center">
                <!-- Language Switcher -->
                <BDropdown 
                  variant="outline-secondary" 
                  size="sm" 
                  class="me-2"
                  :text="languageNames[$i18n.locale]"
                >
                  <BDropdownItem 
                    v-for="item in languageLinks" 
                    :key="item.lang" 
                    :to="item.path"
                    @click="saveUserLanguage(item.lang)"
                  >
                    {{ item.name }}
                  </BDropdownItem>
                </BDropdown>
                
                <!-- Auth Buttons -->
                <template v-if="authStore.isLoggedIn">
                  <a href="/app" class="btn btn-sm btn-outline-dark">
                    {{ t('common.enter-dashboard') }}
                  </a>
                </template>
                <template v-else>
                  <a :href="getRoutePath('/signup')" class="btn btn-sm btn-dark">
                    {{ t('common.get-started') }}
                  </a>
                </template>
              </div>
            </BCollapse>
          </div>
        </nav>
      </header>
    </template>

    <!-- Main Content from pages -->
    <main class="flex-shrink-0" :style="{ 'padding-top': showLayout ? '56px' : '0' }">
      <router-view />
    </main>

    <template v-if="showLayout">
      <!-- Footer -->
      <footer class="bg-light py-5 mt-auto">
        <div class="container">
          <div class="row">
            <div class="col-lg-4 mb-4 mb-lg-0">
              <h5 class="mb-4 logo-font">kikuflow</h5>
              <p class="text-muted">{{ t('components.footer.description') }}</p>
            </div>
            <!-- Product Section -->
            <!-- 
            <div class="col-lg-2 col-md-6 mb-4 mb-lg-0">
              <h5 class="text-uppercase mb-4">{{ t('components.navbar.product') }}</h5>
              <ul class="list-unstyled mb-0">
                <li><router-link :to="getRoutePath('/pricing')" class="text-muted">{{ t('components.navbar.pricing') }}</router-link></li>
              </ul>
            </div>
            -->
            
            <!-- Company Section -->
            <div class="col-lg-2 col-md-6 mb-4 mb-lg-0">
              <h5 class="text-uppercase mb-4">{{ t('components.navbar.company') }}</h5>
              <ul class="list-unstyled mb-0">
                <li><router-link :to="getRoutePath('/about')" class="text-muted">{{ t('components.navbar.about') }}</router-link></li>
                <li><router-link :to="getRoutePath('/contact')" class="text-muted">{{ t('components.navbar.contact') }}</router-link></li>
              </ul>
            </div>
            
            <!-- Follow us Section -->
            <!--
            <div class="col-lg-4">
              <h5 class="text-uppercase mb-4">Follow us</h5>
              <a href="#" class="btn btn-outline-secondary btn-sm me-2">Twitter</a>
              <a href="#" class="btn btn-outline-secondary btn-sm">LinkedIn</a>
            </div>
            -->
          </div>
          <hr class="my-4">
          <div class="d-flex justify-content-between align-items-center">
            <p class="text-muted small mb-0">© 2026 kikuflow. All Rights Reserved.</p>
            <div>
              <router-link :to="getRoutePath('/privacy')" class="text-muted small me-3">{{ t('common.privacy-policy') }}</router-link>
              <router-link :to="getRoutePath('/terms')" class="text-muted small me-3">{{ t('common.terms-of-service') }}</router-link>
              <router-link :to="getRoutePath('/commerce')" class="text-muted small">{{ t('common.commerce') }}</router-link>
            </div>
          </div>
        </div>
      </footer>
    </template>
  </div>

  <!-- BootstrapVueNext Orchestrator 組件 -->
  <BModalOrchestrator />
  <BToastOrchestrator />
  <BPopoverOrchestrator />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useSeoHead } from '../composables/useSeoHead'
import { saveUserLanguage, type SupportedLanguage } from '../utils/language'

const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()

// 所有對外頁的 title / description / canonical / hreflang / og 統一由此注入
useSeoHead()

const showLayout = computed(() => {
  const hiddenRoutes = ['SignIn', 'SignUp', 'SignInRoot', 'SignUpRoot', 'ResetPassword', 'ResetPasswordRoot', 'PublicWorkflowFormIndex', 'PublicWorkflowFormWithId'];
  return !hiddenRoutes.includes(route.name as string);
});

const availableLanguages = ['en', 'zh-tw', 'zh-cn', 'ja']
const languageNames: Record<string, string> = {
  'en': 'English',
  'zh-tw': '繁體中文',
  'zh-cn': '简体中文',
  'ja': '日本語'
}

const getRoutePath = (path: string) => {
  const lang = route.params.lang
  if (lang && availableLanguages.includes(lang as string)) {
    return path === '/' ? `/${lang}` : `/${lang}${path}`
  }
  return path
}

// 當前頁面在指定語系下的路徑（在 /pricing 呼叫 pathForLang('zh-tw') 得 /zh-tw/pricing）
const pathForLang = (newLang: string) => {
  const currentLang = route.params.lang as string | undefined
  let pathWithoutLang = route.path

  if (currentLang) {
    pathWithoutLang = route.path.substring(currentLang.length + 1)
  }
  
  if (!pathWithoutLang.startsWith('/')) {
      pathWithoutLang = '/' + pathWithoutLang
  }

  return `/${newLang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`
}

// 語言選單必須渲染成真正的 <a href>：改用 @click 的 <button> 會讓四個語系頁
// 完全沒有內部連結，Googlebot 找不到而不建立索引（見 docs/ssg-architecture.md）
const languageLinks = computed(() =>
  availableLanguages.map(lang => ({
    lang: lang as SupportedLanguage,
    name: languageNames[lang],
    path: pathForLang(lang)
  }))
)
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Ubuntu:wght@500&display=swap');

.logo-font {
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
}

.navbar-brand:focus, .navbar-brand:active {
  outline: none !important;
  box-shadow: none !important;
}
</style>
